import { useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as BasketIcon } from 'assets/icons/basket.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg';
import { ReactComponent as FilterActiveIcon } from 'assets/icons/filter-active.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import Button from 'components/Button/Button';
import Separator from 'components/Separator/Separator';
import TextField from 'components/TextField/TextField';
import Toggle from 'components/Toggle/Toggle';
import { DATA_SET_UPLOAD_FILE, SEARCH, SHOW_ARCHIVED } from 'constants/formFields';
import { DATA_SETS_MIME_TYPES_ACCEPT } from 'constants/general';
import { BUTTON_VARIANT, SEPARATOR_VARIANT } from 'constants/interfaces';

import type IDataSetsListFilters from './interfaces/IDataSetsListFilters';

import styles from './sass/DataSetsListFilters.module.scss';

function DataSetsListFilters({
  control, meta, onSearchChange, onArchive, isArchiveSelectedItemsVisible, selectedItems, currentTotalItems,
  isActionsVisible, title, subTitle, onAddRecord, isFilterVisible, setIsFiltersBarVisible, isApplyFilter,
  onShowArchiveChange, onDownloadExample, uploadControl,
}: IDataSetsListFilters) {
  const { t } = useTranslation();

  const { count, offset } = meta || {};

  const onChangeRef = useRef<((file: File) => void) | null>(null);

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles[0] && onChangeRef.current) {
      onChangeRef.current(acceptedFiles[0]);
    }
  }, []);

  const {
    getRootProps, getInputProps, open: onOpen,
  } = useDropzone({
    accept: DATA_SETS_MIME_TYPES_ACCEPT,
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
  });

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.title}>
          <div>{title}</div>
          <div className={styles.description}>{subTitle}</div>
        </div>
        <div className={styles.buttonGroup}>
          <Button
            title={t('dataSetsManagement.downloadExample')}
            labelKey="dataSetsManagement.downloadExample"
            startIcon={<DownloadIcon />}
            variant="ghost"
            onClick={onDownloadExample}
            className={styles.exampleButton}
          />
          <Controller
            name={DATA_SET_UPLOAD_FILE}
            control={uploadControl}
            render={({
              field: { onChange },
            }) => {
              onChangeRef.current = onChange;
              return (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <div {...getRootProps()} className={styles.uploadButton}>
                  <input {...getInputProps()} />
                  <Button
                    className={styles.button}
                    title={t('dataSetsManagement.uploadFile.title')}
                    labelKey="dataSetsManagement.uploadFile.title"
                    onClick={onOpen}
                  />
                </div>
              );
            }}
          />
          {isActionsVisible ? (
            <Button
              title={t('dataSetsManagement.addRecord')}
              labelKey="dataSetsManagement.addRecord"
              startIcon={<PlusIcon />}
              variant={BUTTON_VARIANT.primary}
              onClick={onAddRecord}
            />
          ) : null}
        </div>

      </div>
      <Separator className={styles.separator} />
      <div className={styles.content}>
        <div className={styles.filters}>

          <div className={styles.search}>
            <Controller
              control={control}
              name={SEARCH}
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <TextField
                  name={name}
                  value={value}
                  placeholder={t('common.search')}
                  error={error?.message}
                  icon={<SearchIcon />}
                  onChange={(event) => {
                    onChange(event.target.value);
                    onSearchChange({ search: event.target.value, onChange });
                  }}
                  required
                />
              )}
            />
          </div>
          {isFilterVisible && setIsFiltersBarVisible ? (
            <>
              <Button
                title={t('dataSetsManagement.addFilter')}
                labelKey="dataSetsManagement.addFilter"
                startIcon={<FilterIcon />}
                variant="ghost"
                onClick={() => {
                  setIsFiltersBarVisible(true);
                }}
                endIcon={isApplyFilter ? <FilterActiveIcon /> : null}
                className={isApplyFilter ? styles.filter : ''}
              />

              <Separator className={styles.verticalSeparator} position={SEPARATOR_VARIANT.vertical} />
            </>
          ) : null}

          <div className={styles.showArchived}>
            <Controller
              name={SHOW_ARCHIVED}
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Toggle
                  value={name}
                  checked={!!value}
                  name={name}
                  onChange={(e) => {
                    onChange(e.target.checked);
                    onShowArchiveChange?.(e.target.checked);
                  }}
                />
              )}
            />
            <span>{t('dataSetsManagement.showArchived')}</span>

          </div>

          {isArchiveSelectedItemsVisible && isActionsVisible ? (
            <>
              <Separator className={styles.verticalSeparator} position={SEPARATOR_VARIANT.vertical} />
              <Button
                title={t('dataSetsManagement.archiveSelectedItems')}
                labelKey="dataSetsManagement.archiveSelectedItems"
                startIcon={<BasketIcon />}
                variant="ghost"
                onClick={onArchive}
              />
            </>
          ) : null}

        </div>
        <div className={styles.pageNumber}>
          {selectedItems.length > 0 ? t('dataSetsManagement.selectedOf', {
            selected: selectedItems.length,
            total: count,
          }) : t('dataSetsManagement.totalPages', {
            offset: offset ? offset + 1 : 1,
            totalPages: offset ? offset + currentTotalItems
              : currentTotalItems,
            total: count,
          })}

        </div>
      </div>

    </div>
  );
}

export default DataSetsListFilters;
